import React, { useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import * as O from "fp-ts/Option";
import { pipe } from "fp-ts/function";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { CheckBoxGroup } from "src/components/checkbox/checkbox_group";
import { CallToAction } from "src/components/cta/cta_helpers";
import { Error } from "src/components/error/error";
import { Loading } from "src/components/loading/loading";
import { Modal } from "src/components/modal/modal";
import { ProgressCircleGroup } from "src/components/progress_circle/progress_circle_group";
import { ProgressCircleGroupCombined } from "../../components/progress_circle/progress_circle_group_combined";
import { Separator } from "src/components/separator/separator";
import { StepIndicator } from "src/components/step_indicator/step_indicator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { CloudCodeFunction, Viewer } from "src/generated/types";
import { parseError } from "src/utils/parse_error";
import { Routes } from "src/utils/routes";
import { Button } from "src/components/button/button";

import "./dashboard.scss";

/*
 * GraphQL
 */

const DASHBOARD_QUERY = gql`
    query DashboardData {
        viewer {
            user {
                id
                school {
                    id
                    currentReport {
                        id
                        promotionProgress
                        percentCompleted
                        status
                        submissionProgress
                        surveysCompleted
                        constituentGroups
                    }
                    currentReports {
                        edges {
                            node {
                                id
                                type
                                promotionProgress
                                percentCompleted
                                status
                                submissionProgress
                                surveysCompleted
                                constituentGroups
                            }
                        }
                    }
                }
            }
        }
    }
`;

const GENERATE_REPORT_MUTATION = gql`
    mutation GenerateReport($input: CallCloudCodeInput!) {
        callCloudCode(input: $input) {
            result
        }
    }
`;

/*
 * Types
 */

interface ApolloQueryData {
    viewer: Viewer;
}

/*
 * Helpers
 */

// Names the instrument the school signed in for, so a Bundle, FSCI or FFI account can tell which it is in.
export const instrumentTitle = (hasFsci: boolean, hasFfi: boolean): string => {
    if (hasFsci && hasFfi) return "FSCI and FFI Bundle";
    if (hasFsci) return "Flourishing School Culture Instrument";
    if (hasFfi) return "Flourishing Faith Index";
    return "FSCI and FFI Home";
};

/*
 * Main
 */

export const DashboardPage = (): React.ReactElement => {
    const [isPromotionProgressModalOpen, setPromotionProgressModalOpen] = useState(false);
    const [isSurveyProgressModalOpen, setSurveyProgressModalOpen] = useState(false);
    const [isEarlyReportModalOpen, setEarlyReportModalOpen] = useState(false);
    // const [isCombinedReport, setIsCombinedReport] = useState(false);

    const history = useHistory();
    const [generateReport] = useMutation(GENERATE_REPORT_MUTATION);
    const { data, error, loading } = useQuery<ApolloQueryData, Empty>(DASHBOARD_QUERY);

    const generate = async (schoolId: string | undefined): Promise<void> => {
        if (!schoolId) {
            toast.error("No school ID found, please contact an admin.");
            return;
        }
        try {
            await generateReport({
                variables: {
                    input: {
                        functionName: CloudCodeFunction.GenerateReports,
                        params: {
                            schoolId,
                        },
                    },
                },
            });
            history.push(Routes.report.home);
        } catch (e: any) {
            toast.error(parseError(e));
        }
    };

    if (loading) return <Loading />;
    if (error) return <Error />;

    const fsciReport = data?.viewer.user.school?.currentReports?.edges?.find(
        (edge) => edge?.node?.type === "fsci",
    )?.node;
    const ffiReport = data?.viewer.user.school?.currentReports?.edges?.find((edge) => edge?.node?.type === "ffi")?.node;

    const isCombinedReport = fsciReport && ffiReport;
    const dashboardTitle = instrumentTitle(!!fsciReport, !!ffiReport);

    return pipe(
        O.fromNullable(data?.viewer.user.school?.currentReport),
        O.fold(
            () => (
                <div className="main-page-wrap home">
                    <div className="center">
                        <div className="page-section no-margin title-extra-wrap">
                            <PageTitle>{dashboardTitle}</PageTitle>
                            <Body>Welcome to ACSI’s Flourishing Research Portal.</Body>
                        </div>
                        <Separator />
                        <Error message="There is a problem with your account. Please contact an Admin so they can resolve the issue." />
                    </div>
                </div>
            ),
            (currentReport) => (
                <div className="main-page-wrap home">
                    <div className="center">
                        <div className="page-section no-margin title-extra-wrap">
                            <PageTitle>{dashboardTitle}</PageTitle>
                            <Body>Welcome to ACSI’s Flourishing Research Portal.</Body>
                        </div>
                        <Separator />
                        <div className="page-section">
                            <StepIndicator report={currentReport} />
                        </div>
                        <div className="page-section">
                            <CallToAction currentReport={currentReport} schoolId={data?.viewer.user.school?.id} />
                        </div>
                        <Separator />
                        <div className="page-section">
                            <div className="title-wrap">
                                <SectionTitle large>Promotion Progress</SectionTitle>
                                <span
                                    className="whats-this-text"
                                    onClick={(): void => {
                                        setPromotionProgressModalOpen(true);
                                    }}
                                >
                                    What's this?
                                </span>
                                <Modal
                                    isOpen={isPromotionProgressModalOpen}
                                    setOpen={setPromotionProgressModalOpen}
                                    title="Promotion Progress"
                                >
                                    <Body>
                                        Promotion Progress helps you track when you've successfully invited each
                                        stakeholder group to participate in the assessment with the appropriate link.
                                        Check each box once you've sent out the corresponding invitation.
                                    </Body>
                                </Modal>
                            </div>
                            <div className="checkbox-wrapper flex-wrapper">
                                <CheckBoxGroup promotionProgress={currentReport.promotionProgress} />
                            </div>
                        </div>
                        <Separator />
                        <div className="page-section">
                            <div className="title-wrap">
                                <SectionTitle large>Survey Progress</SectionTitle>
                                <span
                                    className="whats-this-text"
                                    onClick={(): void => {
                                        setSurveyProgressModalOpen(true);
                                    }}
                                >
                                    What's this?
                                </span>
                                <Modal
                                    isOpen={isSurveyProgressModalOpen}
                                    setOpen={setSurveyProgressModalOpen}
                                    title="Survey Progress"
                                >
                                    <Body>
                                        Survey Progress helps you track assessment completion rates for each stakeholder
                                        group, so that you can determine if and when a particular group needs additional
                                        reminders to participate.
                                    </Body>
                                    <Body>
                                        When a stakeholder tracking chart below changes to green, it means that you have
                                        reached a minimum target response level for reporting results by that
                                        stakeholder group (though additional completions are always welcome, if
                                        possible).
                                    </Body>
                                </Modal>
                            </div>
                            <div className="progress-circle-wrapper flex-wrapper">
                                {isCombinedReport ? (
                                    <ProgressCircleGroupCombined
                                        constituentGroups={{
                                            fsci: fsciReport.constituentGroups,
                                            ffi: ffiReport.constituentGroups,
                                        }}
                                        submissionProgress={{
                                            fsci: fsciReport.submissionProgress,
                                            ffi: ffiReport.submissionProgress,
                                        }}
                                        surveysCompleted={{
                                            fsci: fsciReport.surveysCompleted,
                                            ffi: ffiReport.surveysCompleted,
                                        }}
                                    />
                                ) : (
                                    <ProgressCircleGroup
                                        constituentGroups={currentReport.constituentGroups}
                                        submissionProgress={currentReport.submissionProgress}
                                        surveysCompleted={currentReport.surveysCompleted}
                                    />
                                )}
                            </div>
                        </div>
                        <Separator />
                        {(currentReport.status as ReportStatus) === "collectingData" ? (
                            <div className="finalize-early-link-wrap">
                                <span
                                    onClick={(): void => {
                                        setEarlyReportModalOpen(true);
                                    }}
                                >
                                    I Want to Finalize My Report Early
                                </span>
                                <Modal
                                    isOpen={isEarlyReportModalOpen}
                                    setOpen={setEarlyReportModalOpen}
                                    title="This action cannot be undone"
                                >
                                    <Body>
                                        You are able to run your report early, but it is important to note that you may
                                        only run your report one time. Running it early means that the results will not
                                        comprehensively represent the perspectives of your audience. For complete and/or
                                        statistically valid results, you must wait until the survey responses have
                                        reached their minimum thresholds. If you wish to proceed, select Run Report
                                        Early.
                                    </Body>
                                    <Button
                                        onClick={() => {
                                            generate(data?.viewer.user.school?.id);
                                            setEarlyReportModalOpen(false);
                                        }}
                                        variation="secondary"
                                    >
                                        I understand, run the report early
                                    </Button>
                                </Modal>
                            </div>
                        ) : null}
                    </div>
                </div>
            ),
        ),
    );
};
